import { Eye } from "lucide-react";
import { useState } from "react";

import { Button } from "@/client/components/ui/button";
import { impersonationApi } from "@/client/features/admin/impersonation-api";
import { useAuthStore } from "@/client/features/auth/auth-store";

/**
 * Persistent, non-dismissible banner shown whenever the portal is being viewed
 * as a tenant by Mantle staff (read-only). It makes the impersonation obvious at
 * all times and offers the single exit path back to the staff console.
 */
export function ImpersonationBanner() {
  const impersonation = useAuthStore((s) => s.impersonation);
  const [exiting, setExiting] = useState(false);

  if (!impersonation) {
    return null;
  }

  const exit = async () => {
    setExiting(true);
    try {
      const { url } = await impersonationApi.exit();
      window.location.assign(url);
    } catch {
      // Even if the call fails, drop the staffer back to the staff console;
      // the cookie is httpOnly so a reload is the reliable way to re-evaluate.
      setExiting(false);
      window.location.assign("/");
    }
  };

  return (
    <div
      role="status"
      className="mb-4 flex items-start gap-3 rounded-lg border border-[var(--info-solid)] bg-[var(--info-bg)] px-4 py-3 text-sm text-[var(--info-fg)]"
    >
      <Eye className="mt-0.5 size-4 shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="font-medium">
          Viewing as {impersonation.tenantName} &middot; read-only staff view
        </p>
        <p className="text-xs opacity-80">
          You are seeing this tenant&apos;s portal as {impersonation.actingStaffEmail}. Changes are
          disabled.
        </p>
      </div>
      <Button
        variant="outline"
        size="sm"
        className="shrink-0 bg-transparent leading-none"
        onClick={exit}
        disabled={exiting}
      >
        {exiting ? "Exiting…" : "Exit tenant view"}
      </Button>
    </div>
  );
}
